<template>
  <div class="datetime-picker" v-if="scheduleStore.isAvailable">

    <!-- VARIANT: BUTTONS -->
    <template v-if="variant === 'buttons'">
      <!-- Date Buttons -->
      <div class="dt-picker-select">
        <div class="dt-picker-section">
          <label>{{ dateLabel }}</label>
          <div class="btn-options">
            <button v-for="date in visibleDates" :key="date.getTime()"
              :class="['dt-picker-btn dt-date-btn', { active: isSelectedDate(date) }]" @click="scheduleStore.setDate(date)">
              <span class="day-name">{{ formatDayName(date) }}</span>
              <span class="day-date">{{ formatDate(date) }}</span>
            </button>

            <button v-if="hasMoreDates && !datesExpanded" class="dt-picker-btn dt-more-btn" @click="datesExpanded = true">
              +{{ hiddenDatesCount }} more
            </button>
          </div>

          <ErrorNote v-if="showErrors && scheduleStore.errors.selectedDate"
            :message="scheduleStore.errors.selectedDate" />
        </div>

        <div class="dt-picker-section" v-if="scheduleStore.selectedDate">
          <label>{{ timeLabel }}</label>
          <div class="btn-options">
            <button v-for="slot in visibleTimeSlots" :key="slot"
              :class="['dt-picker-btn dt-time-btn', { active: scheduleStore.selectedTime === slot }]"
              @click="scheduleStore.setTime(slot)">
              {{ scheduleStore.formatTimeSlot(slot) }}
            </button>

            <button v-if="hasMoreTimeSlots && !timeSlotsExpanded" class="dt-picker-btn dt-more-btn"
              @click="timeSlotsExpanded = true">
              +{{ hiddenTimeSlotsCount }} more
            </button>
          </div>

          <ErrorNote v-if="showErrors && scheduleStore.errors.selectedTime"
            :message="scheduleStore.errors.selectedTime" />
        </div>
      </div>
    </template>

    <!-- VARIANT: SELECT -->
    <template v-else-if="variant === 'select'">
      <!-- Date Select -->
      <div class="dt-picker-select">
        <label class="dt-picker-section">
          <div class="dt-prefix dt-icon" v-html="calendarIcon"></div>
          <select class="dt-select" :value="scheduleStore.selectedDate"
            @change="scheduleStore.setDate(Number($event.target.value))"
            :class="['picker-select', { 'has-error': showErrors && scheduleStore.errors.selectedDate }]">
            <option :value="null" disabled>Select date</option>
            <option v-for="date in scheduleStore.availableDates" :key="date.getTime()" :value="date.getTime()">
              {{ formatDayName(date) }}, {{ formatDate(date) }}
            </option>
          </select>
          <div class="dt-postfix dt-icon" v-html="chevronIcon"></div>
          <ErrorNote v-if="showErrors && scheduleStore.errors.selectedDate"
            :message="scheduleStore.errors.selectedDate" />
        </label>

        <!-- Time Select -->
        <label class="dt-picker-section" v-if="scheduleStore.selectedDate">
          <div class="dt-prefix dt-icon" v-html="clockIcon"></div>
          <select class="dt-select" :value="scheduleStore.selectedTime" placeholder="Select a date"
            @change="scheduleStore.setTime(Number($event.target.value))"
            :class="['picker-select', { 'has-error': showErrors && scheduleStore.errors.selectedTime }]">
            <option :value="null" disabled>Select time</option>
            <option v-for="slot in scheduleStore.availableTimeSlots" :key="slot" :value="slot">
              {{ scheduleStore.formatTimeSlot(slot, scheduleStore.selectedDate) }}
            </option>
          </select>
          <div class="dt-postfix dt-icon" v-html="chevronIcon"></div>
          <ErrorNote v-if="showErrors && scheduleStore.errors.selectedTime"
            :message="scheduleStore.errors.selectedTime" />
        </label>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onActivated } from 'vue'
import { useScheduleStore } from '../store/schedule'
import ErrorNote from '@client/js/components/ErrorNote.vue'
import calendarIcon from '@client/svg/calendar.svg?raw';
import clockIcon from '@client/svg/clock.svg?raw';
import chevronIcon from '@client/svg/chevron.svg?raw';
import { useDocumentVisibility } from '@vueuse/core'

const props = defineProps({
  variant: {
    type: String,
    default: 'select', // 'buttons' | 'select'
    validator: (v) => ['buttons', 'select'].includes(v)
  },
  initialVisibleDates: {
    type: Number,
    default: 3
  },
  initialVisibleTimeSlots: {
    type: Number,
    default: 3
  },
  dateLabel: {
    type: String,
    default: 'Select Date'
  },
  timeLabel: {
    type: String,
    default: 'Select Time'
  },
  showErrors: {
    type: Boolean,
    default: true
  }
})

const scheduleStore = useScheduleStore()

// Expansion state for buttons variant
const datesExpanded = ref(false)
const timeSlotsExpanded = ref(false)

// Refresh on mount / keep-alive activate
onMounted(() => scheduleStore.refreshSlots())
onActivated(() => scheduleStore.refreshSlots())

// Refresh on visibility change (user returns to tab)
const visibility = useDocumentVisibility();
watch(visibility, (current) => {
  if (current === 'visible') {
    scheduleStore.refreshSlots()
    if (scheduleStore.isSelectionStale) {
      scheduleStore.validate()
    }
  }
})

// Reset expansion when date changes
watch(() => scheduleStore.selectedDate, () => {
  timeSlotsExpanded.value = false
})

// Reset expansion when order type changes (dates recalculated)
watch(() => scheduleStore.settings, () => {
  datesExpanded.value = false
  timeSlotsExpanded.value = false
}, { deep: true })

// Visible dates (limited or all)
const visibleDates = computed(() => {
  if (datesExpanded.value) return scheduleStore.availableDates
  return scheduleStore.availableDates.slice(0, props.initialVisibleDates)
})

const hasMoreDates = computed(() => {
  return scheduleStore.availableDates.length > props.initialVisibleDates
})

const hiddenDatesCount = computed(() => {
  return scheduleStore.availableDates.length - props.initialVisibleDates
})

// Visible time slots (limited or all)
const visibleTimeSlots = computed(() => {
  if (timeSlotsExpanded.value) return scheduleStore.availableTimeSlots
  return scheduleStore.availableTimeSlots.slice(0, props.initialVisibleTimeSlots)
})

const hasMoreTimeSlots = computed(() => {
  return scheduleStore.availableTimeSlots.length > props.initialVisibleTimeSlots
})

const hiddenTimeSlotsCount = computed(() => {
  return scheduleStore.availableTimeSlots.length - props.initialVisibleTimeSlots
})

// Helpers
const isSelectedDate = (date) => {
  return scheduleStore.selectedDate === date.getTime()
}

const formatDayName = (date) => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)

  if (date.getTime() === now.getTime()) return 'Today'

  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow'

  return date.toLocaleDateString('en-US', { weekday: 'short' })
}

const formatDate = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
</script>

<style lang="scss">
.datetime-picker {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dt-picker-section {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

/* Button variant */
.btn-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.dt-picker-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.dt-picker-btn:hover:not(.active) {
  border-color: #007bff;
}

.dt-picker-btn.active {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.dt-date-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
}

.dt-date-btn .day-name {
  font-weight: 600;
  font-size: 0.85rem;
}

.dt-date-btn .day-date {
  font-size: 0.75rem;
  color: #666;
}

.dt-date-btn.active .day-date {
  color: rgba(255, 255, 255, 0.8);
}

.dt-more-btn {
  background: #f5f5f5;
  color: #666;
  font-size: 0.85rem;
}

.dt-more-btn:hover {
  background: #e9e9e9;
}

.dt-picker-select {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.dt-icon {
  display: inline-block;
  width: 1em;
  height: 1em;
  contain: strict;
  box-sizing: content-box !important;
  color: #4f5d68;
}

.dt-icon > svg {
  display: block;
  height: 100%;
  width: 100%;
}

.dt-picker-select .dt-picker-section {
  width: 50%;
  flex: 0 1 50%;
  flex: 1 1 calc(50% - 0.5rem);
  transition: flex 0.2s ease-in-out;
  align-items: center;
  display: inline-grid;
  grid-template: [prefix-start select-start postfix-start] auto [prefix-end select-end postfix-end]/[prefix-start select-start] auto [prefix-end] 1fr [postfix-start] auto [postfix-end select-end];
  isolation: isolate;
  color: #2f373d;
}

.dt-picker-select .dt-picker-section .dt-prefix {
  grid-area: prefix;
  padding: 0 0.75em;
}

.dt-picker-select .dt-picker-section .dt-postfix {
  grid-area: postfix;
  padding: 0 0.75em;
  font-size: 0.875rem;
  margin-right: 0.75rem;
}

.dt-picker-select .dt-picker-section .dt-select {
  grid-area: select;
}
.dt-picker-select .dt-picker-section .dt-postfix {
  transform: rotate(0deg);
  transition: transform 0.15s linear;
}
.dt-picker-select .dt-picker-section .dt-select:focus ~ .dt-postfix {
  transform: rotate(90deg);
}
.dt-picker-select .dt-picker-section select {

  appearance: none;
  background-color: rgba(0, 0, 0, 0);
  color: inherit;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  user-select: none;
  position: relative;
  z-index: 1;
  border: 1px solid #ced7df;
  border-radius: 0.25rem;
  padding: 0.75rem 1.25rem 0.75rem 2.25rem;
  font-weight: 400;
  transition: all 0.15s;
}

.dt-picker-select .dt-picker-section select:hover {
  border-color: #4f5d68;
}

.dt-picker-section select:focus-within:not([disabled]),
.dt-picker-section select:active:not([disabled]) {
  border-color: #2f373d;
  outline-color: #2f373d;
  outline-style:solid;
  outline-width:1px;
}
</style>
